import React, { useState, useEffect } from 'react';
import { ChartLayout, OverviewContent, RelevanceContent, EquityContent, MethodologyContent } from './ChartLayout';
import { KeyMetrics, KeyMetric } from './KeyMetrics';
import { useLocation } from '../../contexts/LocationContext';
import {
  ClimateEquityDataService,
  EquityDataResolution,
  EquityChartType,
  EquityDataPoint
} from '../../services/climateEquityDataService';
import EquityBubbleChart from './EquityBubbleChart';
import EquityScatterChart from './EquityScatterChart';
import EquityHeatmapChart from './EquityHeatmapChart';


interface ChartMetrics {
  totalLocations: number;
  avgEducation: number;
  avgInsurance: number;
  avgEVs: number;
  avgSolar: number;
  correlationEducationEVs: number;
  correlationInsuranceEVs: number;
}

const ClimateEquityGapChart: React.FC = () => {
  const [activeView, setActiveView] = useState('overview');
  const [chartType, setChartType] = useState<EquityChartType>('bubble');
  const [resolution, setResolution] = useState<EquityDataResolution>('tract');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [equityData, setEquityData] = useState<EquityDataPoint[]>([]);
  const [metrics, setMetrics] = useState<ChartMetrics>({
    totalLocations: 0,
    avgEducation: 0,
    avgInsurance: 0,
    avgEVs: 0,
    avgSolar: 0,
    correlationEducationEVs: 0,
    correlationInsuranceEVs: 0
  });

  const { selectedLocation } = useLocation();

  useEffect(() => {
    if (selectedLocation) {
      fetchEquityData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocation, resolution]);

  const fetchEquityData = async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await ClimateEquityDataService.getEquityData(
        selectedLocation,
        resolution,
        2023
      );

      setEquityData(data);

      // Calculate summary statistics
      const stats = ClimateEquityDataService.getDatasetStats(data);
      setMetrics({
        totalLocations: stats.totalLocations,
        avgEducation: stats.avgEducation,
        avgInsurance: stats.avgInsurance,
        avgEVs: stats.avgEVs,
        avgSolar: stats.avgSolar,
        correlationEducationEVs: stats.correlationEducationEVs,
        correlationInsuranceEVs: stats.correlationInsuranceEVs
      });

    } catch (err) {
      console.error('Error fetching equity data:', err);
      setError(`Failed to load climate equity data: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };


  const renderChart = () => {
    // Resolution selector
    const resolutionSelector = (
      <div style={{
        display: 'flex',
        gap: '1rem',
        alignItems: 'center',
        marginBottom: '1rem',
        padding: '0.5rem',
        backgroundColor: '#f8fafc',
        border: '1px solid #e2e8f0',
        borderRadius: '0.5rem'
      }}>
        <label style={{ fontSize: '14px', fontWeight: '500', color: '#374151' }}>
          Geographic Resolution:
        </label>
        <select
          value={resolution}
          onChange={(e) => setResolution(e.target.value as EquityDataResolution)}
          style={{
            padding: '0.25rem 0.5rem',
            border: '1px solid #d1d5db',
            borderRadius: '0.25rem',
            fontSize: '14px',
            backgroundColor: 'white'
          }}
        >
          <option value="city">City Level Analysis</option>
          <option value="tract">Census Tract Level Analysis</option>
        </select>
      </div>
    );

    // Render appropriate chart component based on type
    const renderChartComponent = () => {
      switch (chartType) {
        case 'bubble':
          return <EquityBubbleChart data={equityData} resolution={resolution} loading={loading} error={error} />;
        case 'scatter':
          return <EquityScatterChart data={equityData} resolution={resolution} loading={loading} error={error} />;
        case 'heatmap':
          return <EquityHeatmapChart data={equityData} resolution={resolution} loading={loading} error={error} />;
        default:
          return <EquityBubbleChart data={equityData} resolution={resolution} loading={loading} error={error} />;
      }
    };

    return (
      <div style={{ width: '100%' }}>
        {resolutionSelector}
        {renderChartComponent()}
      </div>
    );
  };

  return (
    <ChartLayout
      title="Climate Action Equity Gap Analysis"
      description="Multi-dimensional analysis revealing the relationship between socioeconomic factors and climate technology adoption across communities."
      chartType={undefined}
      keyMetrics={
        <KeyMetrics>
          <KeyMetric
            value={`${metrics.totalLocations}`}
            label={`${resolution === 'city' ? 'Cities' : 'Census Tracts'} Analyzed`}
            color="#1e3a8a"
          />
          <KeyMetric
            value={`${metrics.avgEducation.toFixed(1)}%`}
            label="Avg Education Level"
            color="#059669"
          />
          <KeyMetric
            value={`${metrics.avgInsurance.toFixed(1)}%`}
            label="Avg Insurance Coverage"
            color="#7c3aed"
          />
          <KeyMetric
            value={`${metrics.avgEVs.toFixed(1)}/1k`}
            label="Avg EVs per 1000"
            color="#f59e0b"
          />
          <KeyMetric
            value={`${(metrics.avgSolar * 1000).toFixed(1)}W`}
            label="Avg Solar per Capita"
            color="#dc2626"
          />
          <KeyMetric
            value={`${metrics.correlationEducationEVs.toFixed(2)}`}
            label="Education-EV Correlation"
            color="#8b5cf6"
          />
        </KeyMetrics>
      }
      chartSelector={{
        options: [
          { value: 'bubble', label: 'Bubble Chart' },
          { value: 'scatter', label: 'Scatter Plot' },
          { value: 'heatmap', label: 'Heat Map' }
        ],
        value: chartType,
        onChange: (value: string) => setChartType(value as EquityChartType)
      }}
      chartContent={renderChart()}
      overviewContent={
        <OverviewContent
          title="Understanding Climate Equity Gaps"
          description="This analysis combines education levels, health insurance coverage, electric vehicle adoption, and solar installations to reveal patterns of climate technology access across communities."
        >
          <p>
            The visualization displays each community's position across four key dimensions:
            education levels (x-axis), solar per capita (y-axis),
            EV adoption (bubble size for bubble chart), and health insurance coverage (color intensity).
          </p>
          <p>
            <strong>Key Insights:</strong> Communities in the upper-right quadrant typically show
            both high educational attainment and strong solar adoption, while
            those in the lower-left may face barriers to accessing clean technology solutions.
          </p>
          <p>
            Outliers - communities that break expected patterns - often represent either
            untapped potential (high education, low solar) or inspiring success stories
            (modest education, high solar adoption).
          </p>
        </OverviewContent>
      }
      relevanceContent={
        <RelevanceContent
          title="Relevance to Climate Action Planning"
          description="This equity analysis directly informs climate policy development and investment prioritization by revealing which communities need targeted support versus those ready for scaled deployment."
        >
          <div className="methodology-details-simple">
            <h5>Policy Targeting</h5>
            <p>
              Identifies communities where climate technology barriers exist despite
              economic capacity, suggesting policy intervention opportunities rather
              than just financial constraints.
            </p>
          </div>
          <div className="methodology-details-simple">
            <h5>Investment Prioritization</h5>
            <p>
              Reveals high-impact investment opportunities by showing where modest
              support could unlock significant climate technology adoption in
              underserved but ready communities.
            </p>
          </div>
          <div className="methodology-details-simple">
            <h5>Success Story Replication</h5>
            <p>
              Highlights communities achieving strong climate action despite
              socioeconomic challenges, providing models for broader replication
              and peer-to-peer learning networks.
            </p>
          </div>
        </RelevanceContent>
      }
      equityContent={
        <EquityContent
          title="Climate Justice Implications"
          description="This analysis reveals how climate technology access intersects with existing socioeconomic inequalities, highlighting both barriers and opportunities for just climate action."
        >
          <div className="methodology-details-simple">
            <h5>Identifying Access Barriers</h5>
            <p>
              Communities with lower education and insurance rates often show
              reduced access to clean technology, even when controlling for income,
              suggesting systemic barriers beyond financial capacity alone.
            </p>
          </div>
          <div className="methodology-details-simple">
            <h5>Avoiding Green Gentrification</h5>
            <p>
              By mapping existing socioeconomic patterns alongside climate technology
              deployment, planners can ensure that clean energy investments strengthen
              rather than displace existing communities.
            </p>
          </div>
          <div className="methodology-details-simple">
            <h5>Community-Centered Solutions</h5>
            <p>
              Success stories in communities with modest socioeconomic indicators
              often involve community ownership models, local workforce development,
              and culturally responsive outreach strategies.
            </p>
          </div>
        </EquityContent>
      }
      methodologyContent={
        <MethodologyContent
          title="Data Integration Methodology"
          description="This analysis combines demographic, economic, and climate technology data from multiple authoritative sources to create a comprehensive equity assessment."
          dataSources={[
            'American Community Survey (ACS) - Educational attainment by race/ethnicity',
            'Census Bureau Small Area Health Insurance Estimates',
            'DMV Electric Vehicle Registration Data (2023)',
            'California Distributed Generation Statistics - Solar PV installations',
            'Census Bureau Population Estimates'
          ]}
          methodology={[
            'Normalize all data to common geographic units (census tracts or cities)',
            'Calculate derived metrics: education percentages, insurance coverage rates, per-capita technology adoption',
            'Apply population weighting for accurate community-level aggregations',
            'Implement correlation analysis to identify equity patterns and outliers',
            'Validate geographic matching using FIPS codes and fuzzy location name matching'
          ]}
        />
      }
      activeView={activeView}
      onViewChange={setActiveView}
      showRelevanceEquity={true}
      className="alternating-bg"
    />
  );
};

export default ClimateEquityGapChart;